import type { QueryClient } from "@tanstack/react-query";
import type { OrderView } from "@townsy/contracts";
import { markPropertyStatsStale } from "../api/endpoints";
import { adKeys } from "../api/advertisingQueries";
import { queryKeys } from "../api/queries";
import { rewardKeys } from "../api/rewardsQueries";
import { storeOffersKeys } from "../api/storeOffers";
import type { TownSession } from "../app/townSession";
import type { ToastAction } from "../state/store";

/**
 * What changes when the SERVER reports an order fulfilled (or otherwise settled): balances,
 * ownership, the home, inventory, listings and the order list are re-read from the API; the 3D town
 * re-reads the property so the sign/placement and height update. Nothing is computed locally.
 */
export function refreshAfterOrder(order: Pick<OrderView, "orderId" | "kind" | "status" | "propertyId">, qc: QueryClient, session: TownSession | null): void {
  const inv = (queryKey: readonly unknown[]) => void qc.invalidateQueries({ queryKey });
  inv(queryKeys.wallets);
  inv(["wallets", "history"]);
  inv(queryKeys.myOrders);
  inv(queryKeys.order(order.orderId));
  // A billboard order holds its days while it is open and releases them when it expires or is canceled.
  if (order.kind === "BILLBOARD_RENTAL") inv(adKeys.availabilityAll);
  // A campaign's budget changes only on fulfilment, but a refused or refunded funding shows on the campaign too.
  if (order.kind === "CAMPAIGN_FUNDING") {
    inv(rewardKeys.myCampaigns);
    // ADR-0027: the store screen's budget and status (a funded offer may have started by itself).
    inv(storeOffersKeys.all);
  }
  if (order.status !== "fulfilled") return;
  switch (order.kind) {
    case "COMMERCIAL_ACQUISITION":
      inv(queryKeys.properties);
      if (order.propertyId) {
        inv(queryKeys.myPermissions(order.propertyId));
        inv(queryKeys.propertyHistory(order.propertyId));
        // Statistics now cover a new ownership interval; the route is HTTP-cached for 30 s, so the next read bypasses that cache.
        markPropertyStatsStale(order.propertyId);
        inv(queryKeys.propertyStats(order.propertyId));
        if (session) void session.refreshProperty(order.propertyId);
        else inv(queryKeys.property(order.propertyId));
      }
      break;
    case "RESIDENTIAL_PLOT":
      inv(queryKeys.realEstateListings);
      inv(queryKeys.properties);
      inv(queryKeys.myHome);
      inv(queryKeys.inventory);
      if (order.propertyId) {
        markPropertyStatsStale(order.propertyId);
        inv(queryKeys.propertyStats(order.propertyId));
        if (session) void session.refreshProperty(order.propertyId);
        else inv(queryKeys.property(order.propertyId));
      }
      break;
    case "HOME_UPGRADE":
      inv(queryKeys.myHome);
      inv(queryKeys.properties);
      if (order.propertyId) {
        if (session) session.afterHomeChanged(order.propertyId, null);
        inv(queryKeys.property(order.propertyId));
      }
      break;
    case "CATALOG_ITEM":
      inv(queryKeys.inventory);
      break;
    case "BILLBOARD_RENTAL":
      // The booked days, the board list (what shows today) and the rental's statistics row.
      inv(adKeys.billboards);
      inv(adKeys.availabilityAll);
      inv(adKeys.stats);
      break;
    case "CAMPAIGN_FUNDING":
      // The budget meter, the activation blockers and (once active) the public task lists.
      inv(rewardKeys.myCampaigns);
      inv(["rewards", "report"]);
      inv(rewardKeys.earn);
      inv(rewardKeys.propertyOffersAll);
      break;
  }
}

/** Toast copy for a fulfilled order (shown once per order). */
export function fulfilledToastText(order: Pick<OrderView, "kind" | "description" | "receiptNumber">): string {
  const what = order.description || "Your purchase";
  const tail =
    order.kind === "COMMERCIAL_ACQUISITION"
      ? " The building is yours. Set up your store now: your name, logo, website and the sign inside, on one screen. Choose its interior now or any time, free."
      : order.kind === "RESIDENTIAL_PLOT"
        ? " Your home starts as a tent."
        : order.kind === "CATALOG_ITEM"
          ? " It is in your storage: place it from My home."
          : order.kind === "BILLBOARD_RENTAL"
            ? " Your billboard days are booked; the board switches to your design at 00:00 UTC on the first day."
            : order.kind === "CAMPAIGN_FUNDING"
              ? " The amount was added to your campaign's budget."
              : "";
  return `${what}: purchase complete${order.receiptNumber ? ` (receipt ${order.receiptNumber})` : ""}.${tail}`;
}

/** A purchase that completed this long ago or less is still announced when a page first sees it (e.g. back from 3D Secure). */
export const RECENT_FULFILMENT_MS = 5 * 60_000;

/**
 * Whether a page announces a fulfilled order ("…: purchase complete"): when it watched the order change
 * to fulfilled, or when it first sees it already fulfilled within RECENT_FULFILMENT_MS (the browser was
 * away at the card check) — but never when this browser already announced it for the account (another
 * page of the same purchase, a reload, the receipt opened in a new tab).
 */
export function shouldAnnounceFulfilled(x: { firstSeen: boolean; fulfilledAt: string | null | undefined; now: number; alreadyAnnounced: boolean }): boolean {
  if (x.alreadyAnnounced) return false;
  if (!x.firstSeen) return true;
  const at = Date.parse(x.fulfilledAt ?? "");
  return Number.isFinite(at) && x.now - at < RECENT_FULFILMENT_MS;
}

export interface FulfilledToastHandlers {
  viewReceipt(orderId: string): void;
  /** Opens the store interior picker for a store the buyer now owns. */
  chooseInterior?(propertyId: string): void;
  /** Opens "Set up your store" (ADR-0020) for a store the buyer now owns. */
  setUpStore?(propertyId: string): void;
}

/**
 * The buttons on the "purchase complete" toast: a bought store gets "Set up your store" first (ADR-0020, founder
 * feedback 2026-10-14: the primary step right after buying), then "Choose interior" (ADR-0008 #4); every order gets
 * "View receipt".
 */
export function fulfilledToastActions(
  order: { orderId: string; kind: OrderView["kind"] | null; propertyId?: string | null },
  h: FulfilledToastHandlers,
): ToastAction[] {
  const actions: ToastAction[] = [];
  const propertyId = order.propertyId;
  if (order.kind === "COMMERCIAL_ACQUISITION" && propertyId && h.setUpStore) {
    const setUp = h.setUpStore;
    actions.push({ label: "Set up your store", run: () => setUp(propertyId) });
  }
  if (order.kind === "COMMERCIAL_ACQUISITION" && propertyId && h.chooseInterior) {
    const choose = h.chooseInterior;
    actions.push({ label: "Choose interior", run: () => choose(propertyId) });
  }
  actions.push({ label: "View receipt", run: () => h.viewReceipt(order.orderId) });
  return actions;
}
